import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, ChevronDown } from "lucide-react";
import { SERVICES } from "@/data/content";
import { Cta } from "@/components/site/ui";
import { LogoMark } from "@/components/site/LogoMark";
import { useAuth } from "@/lib/auth";

const MAIN_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

const SERVICES_ACTIVE_PATHS = ["/services", ...SERVICES.map((s) => `/${s.slug}`)];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();
  const { user, openAuth } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [pathname]);

  const openServices = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setServicesOpen(false), 180);
  };

  const linkCls = (active) =>
    `hover-underline text-sm font-medium py-1 transition-colors duration-300 ${
      scrolled
        ? active
          ? "text-[#1C1A17]"
          : "text-[#0A0A0A]/80 hover:text-[#1C1A17]"
        : active
        ? "text-white"
        : "text-white/75 hover:text-white"
    }`;

  const isServicesActive = SERVICES_ACTIVE_PATHS.includes(pathname);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-black/5 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="logo-link"
          className="inline-flex items-center gap-3 whitespace-nowrap"
        >
          <LogoMark size={38} className="shrink-0" />
          <span className={`font-head text-2xl font-extrabold tracking-tight ${scrolled ? "text-[#1C1A17]" : "text-white"}`}>
            SPHERIO LIVING<span className="text-[#B8935A]">.</span>
          </span>
        </Link>

        {/* Desktop nav — right aligned, Sign in adjacent to Contact */}
        <nav className="hidden lg:flex items-center gap-7 ml-auto">
          <Link to="/" data-testid="nav-home" className={linkCls(pathname === "/")}>Home</Link>
          <Link to="/about" data-testid="nav-about" className={linkCls(pathname === "/about")}>About</Link>

          <div
            className="relative"
            onMouseEnter={openServices}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              data-testid="nav-services-trigger"
              onClick={() => setServicesOpen((v) => !v)}
              aria-expanded={servicesOpen}
              aria-haspopup="menu"
              className={`inline-flex items-center gap-1 ${linkCls(isServicesActive)}`}
            >
              Services
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            <div
              role="menu"
              onMouseEnter={openServices}
              onMouseLeave={scheduleClose}
              className={`absolute right-0 top-full pt-4 transition-[opacity,transform] duration-300 ${
                servicesOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
              }`}
            >
              <div className="w-[300px] rounded-2xl bg-white shadow-2xl shadow-black/10 border border-black/5 p-2">
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/${s.slug}`}
                    data-testid={`nav-service-${s.slug}`}
                    role="menuitem"
                    className={`flex items-baseline gap-3 rounded-xl px-4 py-3 transition-colors ${
                      pathname === `/${s.slug}` ? "bg-[#F5F1EA] text-[#1C1A17]" : "text-[#0A0A0A] hover:bg-[#F5F1EA] hover:text-[#1C1A17]"
                    }`}
                  >
                    <span className="font-head text-xs text-[#1C1A17] w-6">{s.index}</span>
                    <span className="flex-1">
                      <span className="block text-sm font-semibold">{s.title}</span>
                      <span className="block text-xs text-[#5C574F] mt-0.5">{s.tagline}</span>
                    </span>
                  </Link>
                ))}
                <div className="mt-1 border-t border-black/5">
                  <Link
                    to="/services"
                    data-testid="nav-service-all"
                    className="flex items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold text-[#0A0A0A] hover:text-[#1C1A17]"
                  >
                    View all services
                    <span className="text-[#1C1A17]">→</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <Link to="/portfolio" data-testid="nav-portfolio" className={linkCls(pathname === "/portfolio")}>Portfolio</Link>
          <Link to="/blog" data-testid="nav-blog" className={linkCls(pathname.startsWith("/blog"))}>Blog</Link>
          <Link to="/contact" data-testid="nav-contact" className={linkCls(pathname === "/contact")}>Contact</Link>

          {user ? (
            <Link
              to={user.role === "admin" ? "/admin" : "/account"}
              data-testid="nav-account-link"
              className={`inline-flex items-center gap-2 text-sm font-medium transition-colors ${scrolled ? "text-[#0A0A0A] hover:text-[#1C1A17]" : "text-white/85 hover:text-white"}`}
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full object-cover" />
              ) : (
                <User className="h-4 w-4" />
              )}
              <span className="max-w-[100px] truncate">{user.name?.split(" ")[0] || "Account"}</span>
            </Link>
          ) : (
            <button
              type="button"
              onClick={openAuth}
              data-testid="nav-signin"
              className={linkCls(false)}
            >
              Sign in
            </button>
          )}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden p-2 -mr-2 ${scrolled || open ? "text-[#0A0A0A]" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div className={`lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl transition-[max-height] duration-500 ${open ? "max-h-[720px] border-t border-black/5" : "max-h-0"}`}>
        <div className="px-6 py-6 flex flex-col gap-1">
          {MAIN_LINKS.slice(0, 2).map((l) => (
            <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase()}`} className="py-3 text-lg font-medium border-b border-black/5 text-[#0A0A0A]">
              {l.label}
            </Link>
          ))}

          <button
            type="button"
            data-testid="mobile-services-toggle"
            onClick={() => setMobileServicesOpen((v) => !v)}
            className="py-3 text-lg font-medium border-b border-black/5 text-[#0A0A0A] flex items-center justify-between"
          >
            Services
            <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
          </button>
          <div className={`overflow-hidden transition-[max-height] duration-500 ${mobileServicesOpen ? "max-h-[400px]" : "max-h-0"}`}>
            <div className="pl-3 py-2 flex flex-col">
              {SERVICES.map((s) => (
                <Link key={s.slug} to={`/${s.slug}`} data-testid={`mobile-service-${s.slug}`} className="py-2.5 text-base text-[#0A0A0A]/80 hover:text-[#1C1A17]">
                  <span className="text-[#1C1A17] font-head text-xs mr-3">{s.index}</span>{s.title}
                </Link>
              ))}
              <Link to="/services" className="py-2.5 text-base font-semibold text-[#1C1A17]">View all services →</Link>
            </div>
          </div>

          {MAIN_LINKS.slice(2).map((l) => (
            <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase()}`} className="py-3 text-lg font-medium border-b border-black/5 text-[#0A0A0A]">
              {l.label}
            </Link>
          ))}

          {user ? (
            <Link to={user.role === "admin" ? "/admin" : "/account"} data-testid="mobile-account-link" className="py-3 text-lg font-medium border-b border-black/5 text-[#0A0A0A]">
              {user.role === "admin" ? "Admin dashboard" : "My account"}
            </Link>
          ) : (
            <button
              type="button"
              onClick={openAuth}
              data-testid="mobile-signin"
              className="py-3 text-lg font-medium border-b border-black/5 text-[#0A0A0A] text-left"
            >
              Sign in
            </button>
          )}
          <Cta to="/book-consultation" className="mt-5 justify-center" data-testid="mobile-book-cta">
            Book Free Consultation
          </Cta>
        </div>
      </div>
    </header>
  );
};
